import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const Num = ({ label, value, onChange, testId, step = "1", min = "0" }) => (
  <label className="text-xs block">{label}
    <Input type="number" min={min} step={step} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}/>
  </label>
);

const TierList = ({ tiers, setTiers }) => (
  <div className="space-y-2" data-testid="tier-list">
    <div className="text-sm font-semibold">Staffelpreise nach Aufenthaltsdauer</div>
    <p className="text-[11px] text-muted-foreground">Preis pro Nacht ab der angegebenen Anzahl Nächte. Beispiel: ab 3 Nächten 69 € → 3 Nächte = 207 €.</p>
    {tiers.map((t, i) => (
      <div key={i} className="flex gap-2 items-end">
        <Num label="ab Nächten" min="1" value={t.min_nights} onChange={(v) => setTiers(tiers.map((x, j) => (j === i ? { ...x, min_nights: v } : x)))} testId={`tier-nights-${i}`}/>
        <Num label="€ / Nacht" value={t.price} onChange={(v) => setTiers(tiers.map((x, j) => (j === i ? { ...x, price: v } : x)))} testId={`tier-price-${i}`}/>
        <Button variant="ghost" size="icon" onClick={() => setTiers(tiers.filter((_, j) => j !== i))} data-testid={`tier-del-${i}`}><Trash size={16}/></Button>
      </div>
    ))}
    <Button variant="outline" size="sm" onClick={() => setTiers([...tiers, { min_nights: "", price: "" }])} data-testid="tier-add"><Plus size={14}/> Stufe</Button>
  </div>
);

const SeasonList = ({ seasons, setSeasons }) => {
  const upd = (i, k, v) => setSeasons(seasons.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2" data-testid="season-list">
      <div className="text-sm font-semibold">Saison- & Sonderzeiträume</div>
      <p className="text-[11px] text-muted-foreground">Gilt vor Staffelpreisen. Preis oder Mindestaufenthalt kann leer bleiben.</p>
      {seasons.map((s, i) => (
        <div key={s.id || i} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end border border-border rounded-lg p-2">
          <label className="text-xs md:col-span-2">Name<Input value={s.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`season-name-${i}`}/></label>
          <label className="text-xs">Von<Input type="date" value={s.start} onChange={(e) => upd(i, "start", e.target.value)} data-testid={`season-start-${i}`}/></label>
          <label className="text-xs">Bis<Input type="date" value={s.end} onChange={(e) => upd(i, "end", e.target.value)} data-testid={`season-end-${i}`}/></label>
          <Num label="€ / Nacht" value={s.price ?? ""} onChange={(v) => upd(i, "price", v)} testId={`season-price-${i}`}/>
          <div className="flex gap-1 items-end">
            <Num label="Mind. N." min="1" value={s.min_stay ?? ""} onChange={(v) => upd(i, "min_stay", v)} testId={`season-min-${i}`}/>
            <Button variant="ghost" size="icon" onClick={() => setSeasons(seasons.filter((_, j) => j !== i))} data-testid={`season-del-${i}`}><Trash size={16}/></Button>
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => setSeasons([...seasons, { name: "", start: "", end: "", price: "", min_stay: "" }])} data-testid="season-add"><Plus size={14}/> Zeitraum</Button>
    </div>
  );
};

const OccupancyBox = ({ occ, setOcc }) => (
  <div className="space-y-2 border border-border rounded-lg p-3" data-testid="occupancy-box">
    <label className="flex items-center gap-2 text-sm font-semibold">
      <input type="checkbox" checked={occ.enabled} onChange={(e) => setOcc({ ...occ, enabled: e.target.checked })} data-testid="occ-enabled"/>Zuschläge nach Personen
    </label>
    {occ.enabled && (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <Num label="Im Grundpreis: Erwachsene" min="1" value={occ.base_adults} onChange={(v) => setOcc({ ...occ, base_adults: v })} testId="occ-base"/>
        <Num label="je weiterer Erwachsener €" value={occ.extra_adult} onChange={(v) => setOcc({ ...occ, extra_adult: v })} testId="occ-adult"/>
        <Num label="Kostenlose Kinder 0–12" value={occ.free_children} onChange={(v) => setOcc({ ...occ, free_children: v })} testId="occ-free"/>
        <Num label="je weiteres Kind 0–12 €" value={occ.child_fee} onChange={(v) => setOcc({ ...occ, child_fee: v })} testId="occ-child"/>
        <Num label="je Kind 13–17 €" value={occ.teen_fee} onChange={(v) => setOcc({ ...occ, teen_fee: v })} testId="occ-teen"/>
        <label className="text-xs">Berechnung
          <select className="w-full h-9 border border-border rounded-md px-2 text-sm bg-background" value={occ.per} onChange={(e) => setOcc({ ...occ, per: e.target.value })} data-testid="occ-per">
            <option value="night">pro Nacht</option><option value="stay">einmal pro Aufenthalt</option>
          </select>
        </label>
      </div>
    )}
  </div>
);

export const RatePlanEditor = ({ slug, plan, onSaved }) => {
  const [loaded, setLoaded] = useState(null);
  const [base, setBase] = useState("");
  const [maxG, setMaxG] = useState("");
  const [defMin, setDefMin] = useState(1);
  const [single, setSingle] = useState(0);
  const [tiers, setTiers] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [occ, setOcc] = useState(plan?.occupancy);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!plan || plan.slug === loaded) return;
    setBase(plan.base_price); setMaxG(plan.max_guests); setDefMin(plan.default_min_stay); setSingle(plan.single_discount_pct);
    setTiers(plan.los_tiers); setSeasons(plan.seasons); setOcc(plan.occupancy); setLoaded(plan.slug);
  }, [plan, loaded]);

  const save = async () => {
    const n = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const body = {
      base_price: n(base), max_guests: n(maxG), default_min_stay: n(defMin) || 1, single_discount_pct: n(single) || 0,
      los_tiers: tiers.map((t) => ({ min_nights: n(t.min_nights), price: n(t.price) })),
      seasons: seasons.map((s) => ({ id: s.id, name: s.name, start: s.start, end: s.end, price: n(s.price), min_stay: n(s.min_stay) })),
      occupancy: Object.fromEntries(Object.entries(occ).map(([k, v]) => [k, k === "enabled" || k === "per" ? v : Number(v)])),
    };
    setSaving(true);
    try { await api.put(`/rates/${slug}/plan`, body); toast.success("Preisregeln gespeichert – sofort auf der Homepage aktiv"); setLoaded(null); onSaved(); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(Array.isArray(d) ? d.map((x) => `${x.loc?.slice(-1)[0]}: ${x.msg}`).join(", ") : d || "Speichern fehlgeschlagen"); }
    setSaving(false);
  };

  if (!plan || !occ) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-5" data-testid="rate-plan-editor">
      <h3 className="font-semibold">Preisregeln</h3>
      <p className="text-[11px] text-muted-foreground">Rangfolge je Nacht: 1. Tagespreis im Kalender · 2. Saison/Sonderzeitraum · 3. Staffelpreis · 4. Standardpreis.</p>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Num label="Standardpreis / Nacht €" value={base} onChange={setBase} testId="plan-base"/>
        <Num label="Max. Gäste" min="1" value={maxG} onChange={setMaxG} testId="plan-max"/>
        <Num label="Standard-Mindestaufenthalt" min="1" value={defMin} onChange={setDefMin} testId="plan-defmin"/>
        <Num label="Einzelbelegung: Rabatt %" step="0.5" value={single} onChange={setSingle} testId="plan-single"/>
      </div>
      <TierList tiers={tiers} setTiers={setTiers}/>
      <SeasonList seasons={seasons} setSeasons={setSeasons}/>
      <OccupancyBox occ={occ} setOcc={setOcc}/>
      <Button onClick={save} disabled={saving} className="rounded-xl" data-testid="plan-save">{saving ? "Speichere…" : "Preisregeln speichern"}</Button>
    </div>
  );
};
